'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import {
  FileText, FileDown, Globe, Hash, Link2, MailCheck, UploadCloud,
} from 'lucide-react'
import { CountUp } from '@/components/ui/count-up'
import { EASE, SPRING } from '@/lib/motion'

// The rights-management hero, animated as the lifecycle it describes.
// When the card scrolls into view the capture-path counts fill, then the
// ledger plays chronologically: a post lands pending, the signature
// request goes out, the creator signs and the approved chip STAMPS in
// (spring, scale-down like a rubber stamp), review clears it, and the
// final beat is the part nobody believes until they see it: a revoke
// propagating to every surface. Plays once; reduced motion (and no-JS
// via CSS) resolves to the final ledger state instantly.

const PATHS = [
  { icon: Hash,        label: 'Hashtag',      count: 31, sub: 'organic',     tone: 'text-sky-700   bg-sky-500/10' },
  { icon: Link2,       label: 'Registration', count: 18, sub: 'link · v3',   tone: 'text-violet-700 bg-violet-500/10' },
  { icon: UploadCloud, label: 'Manual',       count: 3,  sub: 'offline doc', tone: 'text-amber-700 bg-amber-500/10' },
]

// Chronological, so the animation IS the state machine: request → sign →
// review → revoke → gone. `stamp` marks the beats that land as a stamp.
const EVENTS = [
  { ts: '09:14', actor: 'system',    event: 'Hashtag ingest · @sky.b',  state: 'pending',  tone: 'bg-amber-500/15 text-amber-700',     stamp: false },
  { ts: '09:14', actor: 'system',    event: 'Awaiting signature · v3',  state: 'awaiting', tone: 'bg-slate-500/15 text-slate-600',     stamp: false },
  { ts: '09:42', actor: '@sky.b',    event: 'Signed · 2-year licence',  state: 'approved', tone: 'bg-emerald-500/15 text-emerald-700', stamp: true },
  { ts: '12:08', actor: 'judge_ana', event: 'Reviewed · brand-safe',    state: 'cleared',  tone: 'bg-emerald-500/15 text-emerald-700', stamp: true },
  { ts: '28d',   actor: 'creator',   event: 'Revoked via email',        state: 'revoked',  tone: 'bg-rose-500/15 text-rose-700',       stamp: true },
  { ts: '28d',   actor: 'system',    event: 'Removed from 4 surfaces',  state: 'hidden',   tone: 'bg-rose-500/15 text-rose-700',       stamp: false },
]

const ROW_BASE_DELAY = 0.55
const ROW_STEP = 0.28

export function RightsLedgerMotion() {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const total = PATHS.reduce((n, p) => n + p.count, 0)

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="relative">
        <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-primary/15 via-transparent to-rose-200/20 blur-2xl" />
        <m.div
          className="overflow-hidden rounded-3xl border bg-card shadow-2xl shadow-primary/10"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.5, ease: EASE }}
        >
          <div className="flex items-center justify-between border-b bg-secondary/40 px-5 py-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <span className="text-[11px] font-medium uppercase tracking-wider text-foreground">Rights ledger · #SummerWithLumen</span>
            </div>
            <span className="rounded-full bg-card px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground tabular-nums">
              <CountUp value={total} suffix=" signed" />
            </span>
          </div>

          {/* Three capture paths converging into the same ledger; the
              counts fill so the volume reads as measured. */}
          <div className="border-b px-5 py-4">
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Capture paths · last 30 days</p>
            <ul className="mt-2.5 grid grid-cols-3 gap-2">
              {PATHS.map((p, i) => {
                const Icon = p.icon
                return (
                  <m.li
                    key={p.label}
                    className="rounded-lg border bg-secondary/30 p-2.5"
                    initial={reduce ? false : { opacity: 0, y: 12 }}
                    animate={inView ? { opacity: 1, y: 0 } : undefined}
                    transition={{ duration: 0.4, ease: EASE, delay: reduce ? 0 : 0.15 + i * 0.09 }}
                  >
                    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${p.tone}`}>
                      <Icon className="h-3 w-3" />
                    </span>
                    <p className="mt-1.5 font-display text-base font-semibold leading-none tabular-nums">
                      <CountUp value={p.count} />
                    </p>
                    <p className="mt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{p.label}</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">{p.sub}</p>
                  </m.li>
                )
              })}
            </ul>
          </div>

          {/* The lifecycle plays in order: request → sign → review →
              revoke → removed everywhere. */}
          <div className="px-5 pt-3 pb-1">
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Recent activity</p>
          </div>
          <ul className="divide-y divide-dashed">
            {EVENTS.map((row, i) => {
              const delay = reduce ? 0 : ROW_BASE_DELAY + i * ROW_STEP
              return (
                <m.li
                  key={row.event}
                  className="grid grid-cols-[44px_1fr_auto] items-center gap-3 px-5 py-2.5 text-xs"
                  initial={reduce ? false : { opacity: 0, x: -10 }}
                  animate={inView ? { opacity: 1, x: 0 } : undefined}
                  transition={{ duration: 0.35, ease: EASE, delay }}
                >
                  <span className="font-mono text-[10px] text-muted-foreground">{row.ts}</span>
                  <div className="min-w-0">
                    <p className="truncate text-foreground">{row.event}</p>
                    <p className="truncate text-[10px] text-muted-foreground">{row.actor}</p>
                  </div>
                  <m.span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${row.tone}`}
                    initial={reduce ? false : row.stamp ? { opacity: 0, scale: 1.45 } : { opacity: 0 }}
                    animate={inView ? { opacity: 1, scale: 1 } : undefined}
                    transition={
                      row.stamp
                        ? { ...SPRING, delay: delay + 0.12 }
                        : { duration: 0.3, ease: EASE, delay: delay + 0.1 }
                    }
                  >
                    {row.state}
                  </m.span>
                </m.li>
              )
            })}
          </ul>

          <m.div
            className="flex flex-wrap items-center justify-between gap-2 border-t bg-secondary/30 px-5 py-3 text-[11px] text-muted-foreground"
            initial={reduce ? false : { opacity: 0 }}
            animate={inView ? { opacity: 1 } : undefined}
            transition={{ duration: 0.4, ease: EASE, delay: reduce ? 0 : ROW_BASE_DELAY + EVENTS.length * ROW_STEP }}
          >
            <span className="inline-flex items-center gap-1.5"><MailCheck className="h-3 w-3" /> 38% DM conv.</span>
            <span className="inline-flex items-center gap-1.5"><Globe className="h-3 w-3" /> 28 locales</span>
            <span className="inline-flex items-center gap-1.5"><FileDown className="h-3 w-3" /> JSON · signed PDF</span>
          </m.div>
        </m.div>
      </div>
    </LazyMotion>
  )
}
